import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react'
import { primaryColors } from '@assets/styles/helpers/colors.styles'
import Button from '@/shared/components/Button/Button'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { downloadBlob } from '@/shared/utils/downloadBlob'
import styles from './GoogleLocation.module.scss'

const FILE_NAME = 'google-review-qr'

/** Large enough for a table tent or a receipt without visible pixels. */
const PRINT_SIZE = 1024

interface ReviewLinkProps {
  reviewUrl: string
}

/**
 * Google's own review form for the venue, as a link to send and a code to
 * print.
 *
 * Every guest gets the same link, whatever they thought of the visit:
 * sending only the happy ones to Google is review gating, which Google's
 * policy forbids.
 */
function ReviewLink({ reviewUrl }: ReviewLinkProps) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const svgRef = useRef<SVGSVGElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  /*
    The print-size canvas exists only for the click that asks for it: kept
    mounted, it is a 1024px square (more at a higher pixel ratio) that
    qrcode.react redraws on every render of the section around it.
  */
  const [isDrawingPng, setIsDrawingPng] = useState(false)

  useEffect(() => {
    if (!isDrawingPng) return
    // Drawn by now: the canvas draws in its own effect, which runs first.
    canvasRef.current?.toBlob(blob => {
      if (blob) downloadBlob(blob, `${FILE_NAME}.png`)
    }, 'image/png')
    setIsDrawingPng(false)
  }, [isDrawingPng])

  const copyLink = () =>
    navigator.clipboard.writeText(reviewUrl).then(
      () =>
        showSnackbar(
          t('ALERTS.REVIEW_LINK_COPIED.TITLE'),
          t('ALERTS.REVIEW_LINK_COPIED.DESCRIPTION'),
          'success'
        ),
      () =>
        showSnackbar(
          t('ALERTS.REVIEW_LINK_COPY_ERROR.TITLE'),
          t('ALERTS.REVIEW_LINK_COPY_ERROR.DESCRIPTION'),
          'error'
        )
    )

  const downloadSvg = () => {
    if (!svgRef.current) return
    // A file opened on its own needs the namespace React leaves out.
    const svg = svgRef.current.cloneNode(true) as SVGSVGElement
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    const markup = new XMLSerializer().serializeToString(svg)
    downloadBlob(
      new Blob([markup], { type: 'image/svg+xml' }),
      `${FILE_NAME}.svg`
    )
  }

  const downloadPng = () => setIsDrawingPng(true)

  const qrProps = {
    value: reviewUrl,
    level: 'M' as const,
    // The four-module quiet zone the QR standard asks for, so a scanner
    // finds the code on a busy print.
    marginSize: 4,
    fgColor: primaryColors.black,
    bgColor: primaryColors.white
  }

  return (
    <div className={styles.review}>
      <div className={styles.linkRow}>
        <a
          className={`body-medium ${styles.link}`}
          href={reviewUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {reviewUrl}
        </a>
        <Button
          label={t('GOOGLE_LOCATION.REVIEWS.COPY')}
          variant="outlined"
          color="primary"
          icon="las la-copy icon-medium"
          onClick={copyLink}
        />
      </div>

      <div className={styles.qrRow}>
        <QRCodeSVG
          ref={svgRef}
          className={styles.qr}
          size={160}
          title={t('GOOGLE_LOCATION.REVIEWS.QR_TITLE')}
          {...qrProps}
        />
        <div className={styles.downloads}>
          <Button
            label={t('GOOGLE_LOCATION.REVIEWS.DOWNLOAD_PNG')}
            variant="text"
            color="primary"
            icon="las la-download icon-medium"
            onClick={downloadPng}
          />
          <Button
            label={t('GOOGLE_LOCATION.REVIEWS.DOWNLOAD_SVG')}
            variant="text"
            color="primary"
            icon="las la-download icon-medium"
            onClick={downloadSvg}
          />
        </div>
      </div>

      <p className={`body-small ${styles.muted}`}>
        {t('GOOGLE_LOCATION.REVIEWS.HINT')}
      </p>

      {/* The PNG is drawn at print size, off screen, from the same value. */}
      {isDrawingPng && (
        <QRCodeCanvas
          ref={canvasRef}
          className={styles.printCanvas}
          size={PRINT_SIZE}
          aria-hidden
          {...qrProps}
        />
      )}
    </div>
  )
}

export default ReviewLink
